/**
 * IndustryMatrixGrid — the full scrollable matrix table.
 * Receives pre-computed data; uses delegated click handler (zero per-cell props).
 */
import React from 'react';
import { Globe, Layers, Grid3X3, Monitor, ChevronDown, ChevronRight } from 'lucide-react';
import {
  stickyHeader, industryColHeader, stickyCell,
  ScopeBadge, CommonCell, IndustryCell,
} from '../IndustryMappingBlocks';
import { useT } from '../../i18n/I18nContext.jsx';

export function IndustryMatrixGrid({
  filteredTree,
  visibleIndustries,
  expanded,
  scopes,
  autoScopeMap,
  isMappedTo,
  handleTableClick,
  toggleExpand,
}) {
  const t = useT();

  return (
    <div style={{
      border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'auto',
      maxHeight: 640, background: 'var(--surface)', position: 'relative'
    }}>
      <table className="industry-matrix-grid" style={{
        borderCollapse: 'separate', borderSpacing: 0,
        width: 'max-content', minWidth: '100%',
        fontSize: 12, tableLayout: 'fixed'
      }}>
        <colgroup>
          <col style={{ width: 320 }} />
          <col style={{ width: 72 }} />
          {visibleIndustries.map(i => <col key={i.IndustryCode} style={{ width: 64 }} />)}
        </colgroup>

        <thead>
          <tr>
            <th style={stickyHeader(0, 320, 4)}>
              {t('erp.industryMatrix.grid.entity', 'Entity')}
              <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 6, fontWeight: 400 }}>
                ({filteredTree.length} {t('erp.industryMatrix.grid.modulesVisible', 'modules visible')})
              </span>
            </th>
            <th style={stickyHeader(320, 72, 3, '#e0f2fe', '#0369a1')}>
              <Globe size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {t('erp.industryMatrix.common', 'Common')}
            </th>
            {visibleIndustries.map(i => (
              <th
                key={i.IndustryCode}
                title={`${i.IndustryName} · ${i.Category || ''} (${i.IndustryCode})`}
                data-testid={`industry-matrix-col-${i.IndustryCode}`}
                style={industryColHeader()}
              >
                <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap', maxHeight: 90, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {i.IndustryName}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody onClick={handleTableClick}>
          {filteredTree.length === 0 && (
            <tr>
              <td colSpan={2 + visibleIndustries.length}
                style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>
                {t('erp.industryMatrix.grid.noMatch', 'No entities match the filter.')}
              </td>
            </tr>
          )}

          {filteredTree.map(m => {
            const isOpen      = expanded.has(m.ModuleId);
            const moduleScope = autoScopeMap[`Module:${m.ModuleId}`] || 'Vertical';
            return (
              <React.Fragment key={m.ModuleId}>
                {!m._hideModuleRow && (
                  <tr className="module-row" style={{ background: 'var(--surface-2)' }}>
                    <td style={stickyCell(0, 320, 'var(--surface-2)')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button
                          onClick={() => toggleExpand(m.ModuleId)}
                          className="erp-btn"
                          style={{ padding: '2px 4px' }}
                          data-testid={`industry-matrix-expand-mod-${m.ModuleId}`}
                        >
                          {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        </button>
                        <Layers size={12} style={{ color: '#3b82f6' }} />
                        <span style={{ fontWeight: 700 }}>{m.ModuleId} · {m.ModuleName}</span>
                        <ScopeBadge scope={moduleScope} />
                      </div>
                    </td>
                    <CommonCell
                      active={scopes.Module?.[m.ModuleId] === 'Common'}
                      entityType="Module"
                      entityId={String(m.ModuleId)}
                      testId={`industry-matrix-common-mod-${m.ModuleId}`}
                    />
                    {visibleIndustries.map(i => (
                      <IndustryCell
                        key={i.IndustryCode}
                        active={isMappedTo(i.IndustryCode, 'Module', m.ModuleId)}
                        commonOverride={scopes.Module?.[m.ModuleId] === 'Common'}
                        industryCode={i.IndustryCode}
                        entityType="Module"
                        entityId={String(m.ModuleId)}
                        testId={`industry-matrix-cell-mod-${m.ModuleId}-${i.IndustryCode}`}
                      />
                    ))}
                  </tr>
                )}

                {(isOpen || m._hideModuleRow) && m.subModules.map(s => {
                  const subScope = autoScopeMap[`SubModule:${s.SubModuleId}`] || 'Vertical';
                  return (
                    <React.Fragment key={s.SubModuleId}>
                      <tr>
                        <td style={stickyCell(0, 320, 'var(--surface)', 28)}>
                          <Grid3X3 size={11} style={{ color: '#8b5cf6', marginRight: 4 }} />
                          <span style={{ fontWeight: 600 }}>{s.SubModuleId} · {s.SubModuleName}</span>
                          <ScopeBadge scope={subScope} />
                        </td>
                        <CommonCell
                          active={scopes.SubModule?.[s.SubModuleId] === 'Common'}
                          entityType="SubModule"
                          entityId={String(s.SubModuleId)}
                          testId={`industry-matrix-common-sub-${s.SubModuleId}`}
                        />
                        {visibleIndustries.map(i => (
                          <IndustryCell
                            key={i.IndustryCode}
                            active={isMappedTo(i.IndustryCode, 'SubModule', s.SubModuleId)}
                            commonOverride={scopes.SubModule?.[s.SubModuleId] === 'Common'}
                            industryCode={i.IndustryCode}
                            entityType="SubModule"
                            entityId={String(s.SubModuleId)}
                            testId={`industry-matrix-cell-sub-${s.SubModuleId}-${i.IndustryCode}`}
                          />
                        ))}
                      </tr>
                      {s.screens.map(sc => {
                        const scScope = autoScopeMap[`Screen:${sc.SeqId}`] || 'Vertical';
                        return (
                          <tr key={sc.SeqId}>
                            <td style={stickyCell(0, 320, 'var(--surface)', 56)}>
                              <Monitor size={10} style={{ color: '#10b981', marginRight: 4 }} />
                              <span>{sc.SeqId} · {sc.MenuTitle}</span>
                              <ScopeBadge scope={scScope} small />
                            </td>
                            <CommonCell
                              active={scopes.Screen?.[sc.SeqId] === 'Common'}
                              entityType="Screen"
                              entityId={String(sc.SeqId)}
                              testId={`industry-matrix-common-scr-${sc.SeqId}`}
                            />
                            {visibleIndustries.map(i => (
                              <IndustryCell
                                key={i.IndustryCode}
                                active={isMappedTo(i.IndustryCode, 'Screen', sc.SeqId)}
                                commonOverride={scopes.Screen?.[sc.SeqId] === 'Common'}
                                industryCode={i.IndustryCode}
                                entityType="Screen"
                                entityId={String(sc.SeqId)}
                                testId={`industry-matrix-cell-scr-${sc.SeqId}-${i.IndustryCode}`}
                              />
                            ))}
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}

                {(isOpen || m._hideModuleRow) && m.directScreens.map(sc => {
                  const scScope = autoScopeMap[`Screen:${sc.SeqId}`] || 'Vertical';
                  return (
                    <tr key={sc.SeqId}>
                      <td style={stickyCell(0, 320, 'var(--surface)', 28)}>
                        <Monitor size={10} style={{ color: '#10b981', marginRight: 4 }} />
                        <span>{sc.SeqId} · {sc.MenuTitle}</span>
                        <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 6 }}>
                          ({t('erp.industryMatrix.noSubModule', 'no sub-module')})
                        </span>
                        <ScopeBadge scope={scScope} small />
                      </td>
                      <CommonCell
                        active={scopes.Screen?.[sc.SeqId] === 'Common'}
                        entityType="Screen"
                        entityId={String(sc.SeqId)}
                        testId={`industry-matrix-common-scr-${sc.SeqId}`}
                      />
                      {visibleIndustries.map(i => (
                        <IndustryCell
                          key={i.IndustryCode}
                          active={isMappedTo(i.IndustryCode, 'Screen', sc.SeqId)}
                          commonOverride={scopes.Screen?.[sc.SeqId] === 'Common'}
                          industryCode={i.IndustryCode}
                          entityType="Screen"
                          entityId={String(sc.SeqId)}
                          testId={`industry-matrix-cell-scr-${sc.SeqId}-${i.IndustryCode}`}
                        />
                      ))}
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
